import React, { createContext, useEffect, useMemo, useState } from 'react'
import { FinpromStepKeys } from 'pages/Finprom/constants'
import {
  finpromCoolOffPreferenceKey,
  finpromVerificationStepsPreferenceKey,
  type FinpromCoolOffStatus,
  type FinpromVerificationStepsStatus,
} from 'pages/Finprom/finpromPreferenceData'
import { FinpromStep } from 'pages/Finprom/types'
import { getIsFinpromCountry } from 'pages/Finprom/utils'
import { AccountPreferenceData } from 'types/accounts'

interface FinpromContextShared {
  isEnabled: boolean
  isFetched: boolean
  isPhaseTwo: boolean
  refetchData: () => void
}

interface FinpromContextType extends FinpromContextShared {
  isFinpromCountry: boolean
  showBackToBusinessAddress: boolean
  isGoBackToBusinessAddress: boolean
  isWatchingBusinessAddress: boolean
  accountPreferences: AccountPreferenceData
  showFinpromPhaseTwo: boolean
  activeStep: FinpromStep
  setCompanyCountryCode: React.Dispatch<React.SetStateAction<string>>
  setShowBackToBusinessAddress: React.Dispatch<React.SetStateAction<boolean>>
  setIsGoBackToBusinessAddress: React.Dispatch<React.SetStateAction<boolean>>
  setAccountPreferences: React.Dispatch<React.SetStateAction<AccountPreferenceData>>
  setActiveStep: React.Dispatch<React.SetStateAction<FinpromStep>>
  setActiveStepByPreferences: () => void
  onGoBackToBusinessAddress: () => void
  onLeaveBusinessAddress: () => void
}

interface FinpromContextProviderProps extends FinpromContextShared {
  children: React.ReactNode
  companyCountryCodeData: string
  accountPreferencesData: AccountPreferenceData
}

export const finpromContextInitialValues: FinpromContextType = {
  isEnabled: false,
  isFetched: false,
  isPhaseTwo: false,
  showFinpromPhaseTwo: false,
  isFinpromCountry: false,
  showBackToBusinessAddress: false,
  isGoBackToBusinessAddress: false,
  isWatchingBusinessAddress: false,
  accountPreferences: {},
  activeStep: null,
  setCompanyCountryCode: () => {},
  setShowBackToBusinessAddress: () => {},
  setIsGoBackToBusinessAddress: () => {},
  setAccountPreferences: () => {},
  setActiveStep: () => {},
  refetchData: () => {},
  setActiveStepByPreferences: () => {},
  onGoBackToBusinessAddress: () => {},
  onLeaveBusinessAddress: () => {},
}

export const FinpromContext = createContext<FinpromContextType>(finpromContextInitialValues)

export const FinpromContextProvider = ({
  children,
  isEnabled,
  isFetched,
  isPhaseTwo,
  companyCountryCodeData,
  accountPreferencesData,
  refetchData,
}: FinpromContextProviderProps) => {
  const [activeStep, setActiveStep] = useState<FinpromStep>()
  const [companyCountryCode, setCompanyCountryCode] = useState<string>()
  const [accountPreferences, setAccountPreferences] = useState<AccountPreferenceData>()
  const [showBackToBusinessAddress, setShowBackToBusinessAddress] = useState(false)
  const [isGoBackToBusinessAddress, setIsGoBackToBusinessAddress] = useState(false)
  const [isWatchingBusinessAddress, setIsWatchingBusinessAddress] = useState(false)

  const isFinpromCountry = useMemo(
    () => getIsFinpromCountry(companyCountryCode),
    [companyCountryCode]
  )

  const showFinpromPhaseTwo = useMemo(
    () => isFetched && isEnabled && isFinpromCountry && isPhaseTwo,
    [isFetched, isEnabled, isFinpromCountry, isPhaseTwo]
  )

  const setActiveStepByPreferences = () => {
    const coolOffStatus: FinpromCoolOffStatus = accountPreferences?.[finpromCoolOffPreferenceKey]
    if (!coolOffStatus) {
      setActiveStep(FinpromStepKeys.INITIAL)
      return
    }
    const stepStatus: FinpromVerificationStepsStatus =
      accountPreferences?.[finpromVerificationStepsPreferenceKey]
    if (!stepStatus) {
      setActiveStep(FinpromStepKeys.RISK_WARNING)
      return
    }
    setActiveStep(FinpromStepKeys.SELF_CATEGORISATION)
    // TODO: update logic to add conditions for other steps - ONB-1506
  }

  const onGoBackToBusinessAddress = () => {
    setIsGoBackToBusinessAddress(false)
    setShowBackToBusinessAddress(false)
    setIsWatchingBusinessAddress(true)
  }

  const onLeaveBusinessAddress = () => {
    setIsWatchingBusinessAddress(false)
    setActiveStepByPreferences()
  }

  useEffect(() => {
    setCompanyCountryCode(companyCountryCodeData)
  }, [companyCountryCodeData])

  useEffect(() => {
    setAccountPreferences(accountPreferencesData)
  }, [accountPreferencesData])

  const value: FinpromContextType = {
    isEnabled,
    isFetched,
    isFinpromCountry,
    showBackToBusinessAddress,
    isGoBackToBusinessAddress,
    isWatchingBusinessAddress,
    isPhaseTwo,
    accountPreferences,
    activeStep,
    showFinpromPhaseTwo,
    setCompanyCountryCode,
    setShowBackToBusinessAddress,
    setIsGoBackToBusinessAddress,
    onGoBackToBusinessAddress,
    onLeaveBusinessAddress,
    setAccountPreferences,
    setActiveStep,
    setActiveStepByPreferences,
    refetchData,
  }

  return <FinpromContext.Provider value={value}>{children}</FinpromContext.Provider>
}
